import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { AUTO_RETRY_DELAYS_MS, aiFailure } from '../utils/aiIdentify';
import { announceCredits, creditsLeft, useAiCredits } from '../utils/aiCredits';
import { useAuth } from '../context/AuthContext';
import AiCreditsDialog from './credits/AiCreditsDialog';
import DnaReferenceCheck from './ai/DnaReferenceCheck';
import ReferenceCompare from './ai/ReferenceCompare';
import { acceptBlockedReason, certaintyOf, loadAiSettings } from '../utils/aiThresholds';
import { CandidateSafety, SafetyBanner, safetyOf } from './TaxonSafety';
import { ReferencePhotos, ReferenceProfile, Sources, collectSourceIds } from './TaxonDiagnostics';
import { BioProfileView } from './bioprofile/BioProfile';
import { modelName } from '../utils/aiModels';
import { srOnly } from './organic/motion';

export { safetyOf };

/**
 * One AI panel, several call sites: Field capture, the Identify page and the
 * places that link a strain.
 *
 * Four independent copies of a request-render-feedback cycle is how this
 * project ended up with "two independent ~115-line frontend copies of the
 * add-region logic". One component. The safety and reference sections it
 * renders live in TaxonSafety.js / TaxonDiagnostics.js, shared with the
 * Identify game's reveal.
 *
 * SAFETY IS COMPUTED BY THE SERVER, AND SHOWN FIRST
 *
 * routes/taxon_knowledge.py computes toxicity for every candidate at every rank
 * (a genus inherits its deadliest member), and for candidates BELOW the
 * displayed list. This panel renders `result.safety` once, above everything.
 *
 * CERTAINTY AND ONE-CLICK ACCEPT
 *
 * The top candidate is labelled High certainty / Suggested / Uncertain against
 * the thresholds a collection manager set (utils/aiThresholds.js). When the
 * caller passes `onAccept` and the answer is High certainty, a button fills the
 * caller's form with that name. It never saves anything: the person still
 * checks and submits. It is withheld whenever any candidate is flagged as
 * dangerous, so a deadly look-alike can never be accepted in one click.
 *
 * WHAT THIS DELIBERATELY WILL NOT DO
 *
 *  * It never renders an edibility judgement, because the API never sends
 *    one -- and it says so, since an absent field reads as an oversight.
 *  * It shows confidence as a labelled BAND, not a bare percentage.
 *  * It reports the honest failure states -- not configured, no answer,
 *    nothing recognised -- rather than a spinner that never resolves.
 *
 * `onResult(result)` hears every answer, so a caller can keep its `ai_id`.
 *
 * `photoIds` asks about photographs already on a record by their ids
 * (POST /ai/identify {photo_ids}): the server looks their keys up, only for a
 * record the person may identify (Field Mode's record page). `initialResult`
 * opens on an answer given earlier (GET /collections/{id}/ai-identifications),
 * with nothing asked until the caller asks again.
 *
 * A metered account (public guests, external researchers: utils/aiCredits.js)
 * sees what it has left before asking, and with nothing left the panel offers
 * its AI credits instead of Try again: the server answers 402 before any
 * model runs, and asking again would only be refused again.
 *
 * `sawAiFirst` is passed straight through to the feedback row. The caller
 * knows whether the user committed their own answer first; the panel does
 * not, and must not guess.
 *
 * PICKING ANY CANDIDATE (`pickAny`, Field Mode's post-capture summary)
 *
 * With `pickAny` and `onAccept`, every listed candidate gets "Use this name",
 * whatever the certainty, and "None of these" (`onPickNone`) takes the choice
 * back; `picked` names the chosen one when the caller keeps it. The safety
 * rule is the one-click accept's, read on its own: when acceptBlockedReason
 * finds anything dangerous in the answer, nothing is pickable and the warning
 * says to type the name. The server applies the same rule to the link that
 * records the choice as an 'ai_image' identification (routes/ai.py
 * accept_blocked). The High-certainty accept button is not shown then: the
 * pick buttons do its work.
 *
 * `compact` leads with the top name and its match percentage and leaves out
 * the model votes and the nearest reference photographs, for a phone screen
 * with more to show. `requestConfig` goes with the panel's own requests when
 * given (the camera's {keepSession: true}); `onError({status, code})` hears a
 * failure that is not retried, e.g. the 429 of the daily limit, which says
 * so in its own words.
 */

const BANDS = [
  [0.75, 'high'], [0.5, 'moderate'], [0.25, 'low'], [0, 'veryLow'],
];
const BAND_COLOURS = {
  high: 'var(--c-primary-2)', moderate: 'var(--c-warning-text-8)', low: 'var(--c-warning-text-9)', veryLow: 'var(--c-text-muted-2)',
};
const CERTAINTY_STYLE = {
  high: { background: 'var(--c-success-soft)', color: 'var(--c-success-2-fg)', borderColor: 'var(--c-primary-border-2)' },
  suggested: { background: 'var(--c-info-soft)', color: 'var(--c-info-fg)', borderColor: 'var(--c-info-border)' },
  uncertain: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)', borderColor: 'var(--c-warning-2)' },
};

// A cold model's 503 ai_busy is asked again by itself, after these waits
// (utils/aiIdentify.js says why); a manual Try again starts the count afresh.
export { AUTO_RETRY_DELAYS_MS };

function bandOf(confidence) {
  return (BANDS.find(([floor]) => confidence >= floor) || BANDS[3])[1];
}

function rankOf(candidate) {
  const declared = (candidate.candidate_rank || '').toLowerCase();
  if (['species', 'genus', 'family'].includes(declared)) return declared;
  return candidate.taxon_name.trim().includes(' ') ? 'species' : 'genus';
}

export default function AiIdentifyPanel({
  s3Key, metadata, anchor, sawAiFirst = false, onLinkStrain, autoRun = false,
  additionalS3Keys, viewRoles, onAccept, onResult, photoIds, initialResult = null,
  pickAny = false, picked, onPickNone, compact = false, requestConfig, onError,
}) {
  const { t } = useTranslation('ai');
  // The caller's request config only when it has one: an extra argument would
  // change every existing call.
  const post = (url, body) => (requestConfig ? api.post(url, body, requestConfig) : api.post(url, body));
  const [state, setState] = useState(() => {
    if (initialResult) return 'done';
    return autoRun ? 'loading' : 'idle';
  });
  const [result, setResult] = useState(initialResult);
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [accepted, setAccepted] = useState(null);
  const [warmAttempt, setWarmAttempt] = useState(0);
  const user = useAuth()?.user;
  const { metered, credits } = useAiCredits(user);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const retryTimer = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    let live = true;
    loadAiSettings().then(s => { if (live) setThresholds(s.thresholds); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(retryTimer.current); };
  }, []);

  const run = async (attempt = 0) => {
    clearTimeout(retryTimer.current);
    setState('loading');
    setError(null);
    setAccepted(null);
    try {
      const r = await post('/ai/identify', {
        // A record's own photographs by id, else an uploaded photograph's key.
        ...((photoIds || []).length ? { photo_ids: photoIds } : { s3_key: s3Key }),
        metadata: metadata || {},
        top_k: 5,
        // More photographs of the same specimen, fused by the service.
        ...((additionalS3Keys || []).length ? { additional_s3_keys: additionalS3Keys } : {}),
        // The guided capture's role per photograph (cap, gills, habitat), which
        // fixes each one's weight in the fused vector (backend/ai/predict.py).
        ...((viewRoles || []).length ? { view_roles: viewRoles } : {}),
        ...(anchor || {}),
      });
      setResult(r.data);
      setState('done');
      announceCredits(r.data?.credits);
      // The caller keeps the answer's id and model with its record (the field
      // forms: components/field/AiFieldSuggestion.js).
      onResult?.(r.data);
    } catch (e) {
      // A 503 is one of two states (routes/ai.py): no inference backend is
      // configured ("not switched on"), or it is configured but slow, loading
      // its model or failing -- busy, worth trying again. Telling the second
      // the first said a working installation was not set up.
      // A third, desktop only: the AI pack is installed but the launcher would
      // not start it, because other accounts on the computer could have changed
      // it (desktop/launcher.py). Saying "not switched on" there would send the
      // operator to install what is already installed.
      // A fourth (2026-10-13): configured, but the function, its endpoint or the
      // permission to call it is missing (ai_unreachable). No retry will help,
      // so no Try again button, and the server's detail says what is missing.
      // A fifth (2026-10-14): dev or staging, where live identification is not
      // run at all, on purpose (ai_prod_only): it runs only in production.
      // utils/aiIdentify.js aiFailure says which. The daily limit
      // (routes/ai.py AI_DAILY_LIMIT) is said as itself: a "try again" there
      // would only be refused again.
      const failure = aiFailure(e);
      announceCredits(failure.credits);
      if (failure.busy && attempt < AUTO_RETRY_DELAYS_MS.length) {
        if (!mounted.current) return;
        setWarmAttempt(attempt + 1);
        setState('warming');
        retryTimer.current = setTimeout(() => run(attempt + 1), AUTO_RETRY_DELAYS_MS[attempt]);
        return;
      }
      setErrorDetail(failure.detail);
      // `key` names the words shown, so a caller can say them too (the
      // camera's summary, whose request nobody tapped for).
      onError?.({ status: failure.status, code: failure.code, key: failure.key });
      setError(failure.key);
      setState('error');
    }
  };

  // autoRun used to set the loading state and never start the request, so a
  // caller passing it would have shown "Looking…" forever. Start once, on mount.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoRun && !autoStarted.current) {
      autoStarted.current = true;
      run();
    }
  }, [autoRun]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendFeedback = async (agreement) => {
    setFeedbackGiven(agreement);
    try {
      await post(`/ai/identifications/${result.ai_id}/feedback`, {
        agreement,
        saw_ai_first: sawAiFirst,
      });
    } catch {
      // The identification is the record; losing the agreement note is a
      // smaller loss than interrupting the operator over it.
    }
  };

  if (state === 'idle') {
    return (
      <div style={styles.panel}>
        <button type="button" style={styles.runBtn} title={t('tip.run')} onClick={() => run()}>
          {t('run')}
        </button>
        {metered && credits && (
          <p style={styles.muted} data-testid="ai-credits-left">{t('creditsLeft', { count: creditsLeft(credits) })}</p>
        )}
        <p style={styles.disclaimer}>{t('disclaimer.notAnIdentification')}</p>
      </div>
    );
  }

  if (state === 'loading') {
    return <div style={styles.panel}><p style={styles.muted}>{t('running')}</p></div>;
  }

  if (state === 'warming') {
    return (
      <div style={styles.panel}>
        <p style={styles.muted} role="status" data-testid="ai-warming">
          {t('warming', { attempt: warmAttempt, total: AUTO_RETRY_DELAYS_MS.length })}
        </p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={styles.panel}>
        <p style={styles.muted} role="alert" data-testid="ai-error">{t(error)}</p>
        {errorDetail && <p style={styles.version} dir="ltr" data-testid="ai-error-detail">{errorDetail}</p>}
        {(error === 'failed' || error === 'busy') && (
          <button type="button" style={styles.runBtn} title={t('tip.retry')} onClick={() => run()}>
            {t('retry')}
          </button>
        )}
        {error === 'creditsExhausted' && (
          <button type="button" style={styles.runBtn} title={t('tip.buyCredits')} aria-haspopup="dialog"
            data-testid="ai-buy-credits" onClick={() => setCreditsOpen(true)}>
            {t('buyCredits')}
          </button>
        )}
        {creditsOpen && <AiCreditsDialog credits={credits} onClose={() => setCreditsOpen(false)} />}
      </div>
    );
  }

  const predictions = result?.predictions || [];
  const safety = safetyOf(result);
  const top = predictions[0];
  const certainty = top && thresholds ? certaintyOf(result, thresholds) : null;
  const blocked = certainty === 'high' ? acceptBlockedReason(result) : null;
  // Picking any candidate: read on its own, since `certainty` waits for the
  // thresholds to load and the safety rule must not.
  const picking = !!onAccept && pickAny && predictions.length > 0;
  const pickBlocked = picking ? acceptBlockedReason(result) : null;
  const chosenName = picked !== undefined ? picked : accepted;
  const isChosen = name => !!chosenName && String(chosenName).trim().toLowerCase() === String(name).trim().toLowerCase();

  const choose = (candidate) => {
    const chosen = { ...candidate, rank: rankOf(candidate) };
    setAccepted(chosen.taxon_name);
    onAccept(chosen);
  };
  const accept = () => choose(top);
  const chooseNone = () => {
    setAccepted(null);
    onPickNone?.();
  };

  return (
    <div style={styles.panel}>
      <strong style={styles.title}>{t('title')}</strong>

      <SafetyBanner safety={safety} t={t} />

      {result.review?.needs_review && (
        <p data-testid="ai-review-routed" style={styles.caution}>
          {t('review.routed')}{': '}
          {(result.review.reasons || []).map(r => t(`review.reasons.${r}`)).join(', ')}
        </p>
      )}

      {compact && top && (
        <p data-testid="ai-top-match" style={styles.topMatch}>
          <span dir="ltr" style={styles.topName}>{top.taxon_name}</span>
          <strong style={styles.topPct}>{t('pick.match', { pct: Math.round(top.confidence * 100) })}</strong>
        </p>
      )}

      {certainty && (
        <div data-testid="ai-certainty" data-certainty={certainty}
             style={{ ...styles.certainty, ...CERTAINTY_STYLE[certainty] }}>
          <strong>{t(`certainty.${certainty}`, { pct: Math.round(top.confidence * 100) })}</strong>
          <span dir="ltr" style={{ fontStyle: 'italic' }}>{top.taxon_name}</span>
        </div>
      )}

      {picking && pickBlocked && (
        <p data-testid="ai-accept-blocked" role="note" style={styles.caution}>{t('accept.blockedDangerous')}</p>
      )}

      {onAccept && !pickAny && certainty === 'high' && (
        blocked ? (
          <p data-testid="ai-accept-blocked" role="note" style={styles.caution}>{t('accept.blockedDangerous')}</p>
        ) : accepted ? (
          <p data-testid="ai-accepted" style={styles.muted}>{t('accept.done', { taxon: accepted })}</p>
        ) : (
          <div style={styles.acceptRow}>
            <button type="button" data-testid="ai-accept" style={styles.acceptBtn} title={t('tip.accept')} onClick={accept}>
              {t('accept.button', { taxon: top.taxon_name })}
            </button>
            <span style={styles.scopeNote}>{t('accept.note')}</span>
          </div>
        )
      )}

      {result.weights_fitted === false && (
        <p style={styles.caution}>{t('unfitted')}</p>
      )}

      {result.coverage_warning && (
        <p style={styles.caution}>
          {t(`coverage.${result.coverage_warning}`, { defaultValue: '' })}
        </p>
      )}

      {predictions.length === 0 && <p style={styles.muted}>{t('empty')}</p>}

      <ul style={styles.list}>
        {predictions.map((p) => {
          const band = bandOf(p.confidence);
          const sourceIds = collectSourceIds([p.safety, p.reference, p.internal_observations,
                                              p.consistency]);
          return (
            <li key={p.taxon_name} style={styles.item} data-testid="ai-candidate">
              <div style={styles.row}>
                <span style={styles.name}>{p.taxon_name}</span>
                <span style={{ ...styles.band, color: BAND_COLOURS[band] }}>
                  {t(`band.${band}`)} — {Math.round(p.confidence * 100)}%
                </span>
              </div>
              {picking && !pickBlocked && (
                // The name starts with the words on the button (WCAG 2.5.3,
                // so "tap Use this name" works by voice) and goes on to the
                // candidate; the label stays the same when pressed, the state
                // is aria-pressed's (the APG toggle-button pattern).
                <button type="button" data-testid="ai-pick" aria-pressed={isChosen(p.taxon_name)}
                  title={t('tip.pick')} style={isChosen(p.taxon_name) ? styles.pickBtnOn : styles.pickBtn}
                  onClick={() => choose(p)}>
                  {isChosen(p.taxon_name) && <span aria-hidden="true">✓ </span>}
                  {t('pick.button')}
                  <span style={srOnly}>{`: ${p.taxon_name}`}</span>
                </button>
              )}
              <CandidateSafety p={p} t={t} />
              <div style={styles.meta}>
                {p.in_general_data
                  ? (p.in_bank
                      ? t('inBank', { count: p.bank_record_count })
                      : t('notInBank'))
                  : t('notInDictionary')}
              </div>
              {p.prior_effect && (
                <div style={styles.prior}>
                  {t('priorLabel')}: {p.prior_effect}
                </div>
              )}
              <ReferenceProfile p={p} t={t} />
              {/* An AI candidate is an unconfirmed identification: its whole profile under the
                  unconfirmed label; an answer saved before 2026-10-21 carries literature and
                  compounds only (routes/taxon_bioprofile.py). */}
              <BioProfileView profile={p.bioprofile} sources={result.sources} compact confirmed={false}
                              tier={p.bioprofile?.tier === 'full' ? 'full' : 'ai_safe'} testId="ai-bioprofile" />
              <ReferencePhotos taxon={p.taxon_name} t={t} />
              <Sources ids={sourceIds} sources={result.sources} t={t} />
              {(p.strain_matches || []).length > 0 && onLinkStrain && (
                <div style={styles.strains}>
                  {p.strain_matches.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      style={styles.linkBtn}
                      title={t('tip.linkStrain')}
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        if (window.confirm(
                          t('linkStrainConfirm', { code: s.isolate_code }))) {
                          onLinkStrain(s);
                        }
                      }}
                    >
                      {t('linkStrain')}: {s.isolate_code}
                    </button>
                  ))}
                  <span style={styles.scopeNote}>{t('similarScope')}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {picking && !pickBlocked && (
        <div style={styles.acceptRow}>
          {onPickNone && (
            <button type="button" data-testid="ai-pick-none" aria-pressed={!chosenName} title={t('tip.pickNone')}
              style={!chosenName ? styles.pickBtnOn : styles.pickBtn} onClick={chooseNone}>
              {t('pick.none')}
            </button>
          )}
          <span style={styles.scopeNote}>{t('pick.note')}</span>
        </div>
      )}

      {result.unmapped_mass > 0.05 && (
        <p style={styles.caution}>
          {t('unmapped', { pct: Math.round(result.unmapped_mass * 100) })}
        </p>
      )}

      {predictions.length > 0 && (
        <div style={styles.feedback}>
          {feedbackGiven ? (
            <p style={styles.muted}>
              {t('feedback.thanks')}
              {sawAiFirst ? ` ${t('feedback.sawFirst')}` : ''}
            </p>
          ) : (
            <>
              <span style={styles.fbPrompt}>{t('feedback.prompt')}</span>
              <div style={styles.fbRow}>
                {['agree', 'partial', 'disagree', 'unsure'].map(a => (
                  <button
                    key={a}
                    type="button"
                    style={styles.fbBtn}
                    title={{
                      agree: t('tip.feedback.agree'),
                      partial: t('tip.feedback.partial'),
                      disagree: t('tip.feedback.disagree'),
                      unsure: t('tip.feedback.unsure'),
                    }[a]}
                    onClick={() => sendFeedback(a)}
                  >
                    {t(`feedback.${a}`)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <p style={styles.edibility} data-testid="ai-edibility">{t('edibilityWithheld')}</p>
      <p style={styles.disclaimer}>{t('disclaimer.notAnIdentification')}</p>
      {!compact && <ModelVotes result={result} t={t} />}
      {!compact && <NeighbourMatches neighbors={result.diagnostics?.neighbors} t={t} />}
      {!compact && <ReferenceCompare s3Key={s3Key} photoId={s3Key ? null : photoIds?.[0]} user={user}
        requestConfig={requestConfig} />}
      {!compact && <DnaReferenceCheck aiId={result.ai_id} user={user} requestConfig={requestConfig} />}
      {result.model_version && (
        <p style={styles.version}>{t('modelVersion', { version: result.model_version })}</p>
      )}
    </div>
  );
}

/**
 * How each model in the ensemble voted, collapsed under the answer: method,
 * weight in the fused answer, runtime and its own top guess, then the models
 * that said nothing. An older AI pack sends none of this, and then nothing
 * renders -- the fused answer above is the one that counts either way.
 */
export function ModelVotes({ result, t }) {
  const contributions = result?.model_contributions || {};
  const silent = result?.models_silent || [];
  const runtimes = result?.runtimes || {};
  const answered = (result?.models_answered || []).length
    ? result.models_answered : Object.keys(contributions);
  if (answered.length === 0 && silent.length === 0) return null;
  const weightOf = m => (typeof contributions[m]?.weight === 'number' ? contributions[m].weight : -1);
  const rows = [...answered].sort((a, b) => weightOf(b) - weightOf(a));
  return (
    <details data-testid="ai-model-votes" style={styles.votes}>
      <summary style={styles.votesSummary} title={t('tip.votes')}>{t('votes.title')}</summary>
      <ul style={styles.votesList}>
        {rows.map(m => {
          const c = contributions[m] || {};
          const top = (c.top || [])[0];
          return (
            <li key={m} data-testid={`ai-model-vote-${m}`}>
              <strong>{modelName(m)}</strong>
              {c.mode && <> · {t(`votes.mode.${c.mode}`, { defaultValue: c.mode })}</>}
              {typeof c.weight === 'number' && <> · {t('votes.weight', { pct: Math.round(c.weight * 100) })}</>}
              {runtimes[m] && <> · {t(`votes.runtime.${runtimes[m]}`, { defaultValue: runtimes[m] })}</>}
              {' — '}
              {top
                ? <><span dir="ltr" style={{ fontStyle: 'italic' }}>{top.taxon}</span> {Math.round(top.confidence * 100)}%</>
                : t('votes.noAnswer')}
            </li>
          );
        })}
      </ul>
      {silent.length > 0 && (
        <p data-testid="ai-models-silent" style={styles.votesSilent}>
          {t('votes.silent', { models: silent.map(modelName).join(', ') })}
        </p>
      )}
    </details>
  );
}

/**
 * The reference photographs each bank model found nearest the specimen, and the
 * vote they cast (routes/ai.py `diagnostics.neighbors`, backend/ai
 * Ensemble.reference_neighbors). Collapsed: it says what the answer rests on --
 * which photographs in the bank this looks like, and how firmly they agree --
 * not a second answer. Nothing renders when the service sent none.
 */
export function NeighbourMatches({ neighbors, t }) {
  const models = Object.keys(neighbors || {}).filter(m => (neighbors[m]?.nearest || []).length > 0);
  if (models.length === 0) return null;
  const pct = v => (typeof v === 'number' ? `${Math.round(v * 100)}%` : '—');
  return (
    <details data-testid="ai-neighbors" style={styles.votes}>
      <summary style={styles.votesSummary} title={t('tip.neighbors')}>{t('neighbors.title')}</summary>
      <p style={styles.votesSilent}>{t('neighbors.intro')}</p>
      {models.map(m => {
        const block = neighbors[m];
        return (
          <div key={m} data-testid={`ai-neighbors-${m}`} style={{ marginTop: 6 }}>
            <strong>{modelName(m)}</strong>
            {typeof block.bank_size === 'number' && (
              <span> · {t('neighbors.bankSize', { n: block.bank_size.toLocaleString() })}</span>
            )}
            <ol style={styles.votesList}>
              {block.nearest.map((n, i) => (
                <li key={`${n.label}-${i}`}>
                  <span dir="ltr" style={{ fontStyle: 'italic' }}>{n.label}</span>
                  {' — '}{t('neighbors.similarity', { value: typeof n.similarity === 'number'
                    ? n.similarity.toFixed(3) : '—' })}
                </li>
              ))}
            </ol>
            {(block.vote || []).length > 0 && (
              <p style={styles.votesSilent} data-testid={`ai-neighbors-vote-${m}`}>
                {t('neighbors.vote')}:{' '}
                {block.vote.slice(0, 3).map(v => `${v.label} ${pct(v.probability)}`).join(' · ')}
              </p>
            )}
          </div>
        );
      })}
    </details>
  );
}

const styles = {
  panel: {
    padding: 14, borderRadius: 10, background: 'var(--c-info-soft-4)',
    border: '1px solid var(--c-info-soft-5)', display: 'flex', flexDirection: 'column',
    gap: 10,
  },
  title: { fontSize: 14, color: 'var(--c-info-2-fg)' },
  runBtn: {
    minHeight: 44, border: '1px solid var(--c-info-2)', borderRadius: 8,
    background: 'var(--c-surface)', color: 'var(--c-info-2-fg)', font: 'inherit', fontSize: 15,
    cursor: 'pointer', padding: '0 18px', alignSelf: 'flex-start',
  },
  muted: { margin: 0, color: 'var(--c-text-muted-3-fg)', fontSize: 14 },
  certainty: {
    display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 13,
    padding: '6px 10px', borderRadius: 8, border: '1px solid',
  },
  acceptRow: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  // The compact headline: the name, then how well it matches, large.
  topMatch: { margin: 0, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' },
  topName: { fontSize: 18, fontWeight: 700, fontStyle: 'italic', color: 'var(--c-text-2-fg)' },
  topPct: { fontSize: 18, color: 'var(--c-info-2-fg)' },
  // A 44px toggle: outlined, filled once chosen.
  pickBtn: {
    marginTop: 8, minHeight: 44, padding: '0 14px', borderRadius: 8, border: '1px solid var(--c-primary)',
    background: 'var(--c-surface)', color: 'var(--c-primary-fg)', font: 'inherit', fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
  },
  pickBtnOn: {
    marginTop: 8, minHeight: 44, padding: '0 14px', borderRadius: 8, border: '1px solid var(--c-primary)',
    background: 'var(--c-primary)', color: 'var(--c-on-accent)', font: 'inherit', fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
  },
  acceptBtn: {
    minHeight: 44, border: 'none', borderRadius: 8, background: 'var(--c-primary)', color: 'var(--c-on-accent)',
    font: 'inherit', fontSize: 14, padding: '0 14px', cursor: 'pointer',
  },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  item: { padding: 10, background: 'var(--c-surface)', borderRadius: 8, border: '1px solid var(--c-info-soft-3)' },
  row: { display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  name: { fontSize: 15, fontWeight: 600, color: 'var(--c-text-2-fg)', fontStyle: 'italic' },
  band: { fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  meta: { fontSize: 12, color: 'var(--c-text-muted-2-fg)', marginTop: 4 },
  prior: { fontSize: 12, color: 'var(--c-text-muted-3-fg)', marginTop: 4, fontStyle: 'italic' },
  strains: { marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  linkBtn: {
    border: '1px solid var(--c-primary-2)', borderRadius: 6, background: 'var(--c-surface)',
    color: 'var(--c-primary-2-fg)', font: 'inherit', fontSize: 13, padding: '7px 12px',
    cursor: 'pointer', minHeight: 36,
  },
  scopeNote: { fontSize: 11, color: 'var(--c-text-muted-2-fg)' },
  caution: {
    margin: 0, padding: '8px 10px', borderRadius: 6, background: 'var(--c-warning-soft-3)',
    color: 'var(--c-warning-text-3-fg)', fontSize: 12, lineHeight: 1.45,
  },
  feedback: { borderTop: '1px solid var(--c-info-soft-3)', paddingTop: 10 },
  fbPrompt: { fontSize: 13, color: 'var(--c-text-body-2-fg)', display: 'block', marginBottom: 8 },
  fbRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  fbBtn: {
    border: '1px solid var(--c-border-strong-2)', borderRadius: 8, background: 'var(--c-surface)',
    font: 'inherit', fontSize: 13, padding: '9px 12px', cursor: 'pointer',
    // Field Mode's touch floor (an inline height beats index.css's 44px).
    minHeight: 44,
  },
  edibility: { margin: 0, fontSize: 12, color: 'var(--c-warning-text-7-fg)', fontWeight: 600, lineHeight: 1.45 },
  disclaimer: {
    margin: 0, fontSize: 11, color: 'var(--c-text-muted-2-fg)', lineHeight: 1.5,
    borderTop: '1px solid var(--c-info-soft-3)', paddingTop: 8,
  },
  votes: { fontSize: 12, color: 'var(--c-text-muted-3-fg)' },
  votesSummary: { cursor: 'pointer', color: 'var(--c-info-2-fg)' },
  votesList: { margin: '6px 0 0', paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 3 },
  votesSilent: { margin: '6px 0 0' },
  version: { margin: 0, fontSize: 10, color: 'var(--c-text-faint-3-fg)', fontFamily: 'monospace' },
};
